import type { Workout } from "./schemas/workout.js";

// Derive a per-slug "last logged weighted kg" map from a recent-window
// of assembled workouts (newest-first). Drives the history step of
// resolveDefaultWeight at plan-start. Pure — no fetch, no React; the
// caller supplies the window (mobile reuses the EntryHistoryProvider
// cache, web can plug a fresh fetch in once Phase 4 lifts the bug-fix-
// only freeze).
//
// "Last logged" semantics: the most recent weighted set the athlete
// performed on the slug, picked by walking workouts newest-first and
// taking the last weighted set in the first matching entry. We DON'T
// take session-top — the prefill expectation is "the weight I ended on
// last time", which matches the lifter's wallet of carry-forward
// (next set = previous set's number until they change it). Failed sets
// still count: the lifter tried that load.
export function lastLoggedKgsFromWorkouts(
  workouts: ReadonlyArray<Workout>,
  slugs: ReadonlyArray<string>,
): Map<string, number> {
  const out = new Map<string, number>();
  if (slugs.length === 0 || workouts.length === 0) return out;
  const wanted = new Set(slugs);
  for (const w of workouts) {
    for (const entry of w.entries) {
      if (!wanted.has(entry.exerciseId)) continue;
      if (out.has(entry.exerciseId)) continue;
      // Walk the entry's sets back-to-front to find the last weighted
      // logged value. Bodyweight + cardio sets don't expose a `weight`,
      // so they're skipped; the chain treats those slugs as "no
      // history" and falls back to 'blank' at the logger.
      let last: number | undefined;
      for (let i = entry.sets.length - 1; i >= 0; i -= 1) {
        const set = entry.sets[i];
        if (!set) continue;
        if (set.type !== "weighted") continue;
        if (typeof set.weight !== "number") continue;
        if (!Number.isFinite(set.weight)) continue;
        if (set.weight <= 0) continue;
        last = set.weight;
        break;
      }
      if (last !== undefined) out.set(entry.exerciseId, last);
    }
    if (out.size === wanted.size) break;
  }
  return out;
}
